import React, {useMemo} from 'react';
import {useState} from 'react';
import {
	Stack,
	IconButton,
	useTheme,
	Text,
	Theme,
	mergeStyleSets,
} from '@fluentui/react';
import {useTranslation} from 'react-i18next';
import {SearchResultLoading} from './SearchResultCards';

type SearchGroupProps = {
	entityName: string;
	loading: boolean;
	childrenAll?: JSX.Element[];
	initialShownItems?: number;
	detlaShownItems?: number;
};

const NUM_DEFAULT_ITEMS = 25;
const NUM_DEFAULT_DELTA = 25;

export const SearchGroup: React.FC<SearchGroupProps> = ({
	entityName,
	childrenAll,
	initialShownItems,
	detlaShownItems,
	loading,
}) => {
	const {t} = useTranslation('features/search');
	const theme = useTheme();
	const classNames = searchGroupClassNames(theme);
	const [limitResult, setLimitResult] = useState(
		initialShownItems ?? NUM_DEFAULT_ITEMS,
	);

	const childrenFinal: JSX.Element[] = loading
		? new Array(3).fill(<SearchResultLoading />)
		: childrenAll ?? [];

	return (
		<Stack className={classNames.group}>
			<Stack horizontal tokens={{childrenGap: 5}}>
				<Text className={classNames.heading}>{entityName}</Text>
				<Text className={classNames.itemCount}>
					{childrenFinal.length > 99 ? '99+' : childrenFinal.length}
				</Text>
			</Stack>
			<Stack className={classNames.children}>
				{childrenFinal.length === 0 && (
					<div style={{padding: 10}}>{t('NoResult')}</div>
				)}
				{childrenFinal.slice(0, limitResult)}
			</Stack>
			{childrenFinal.length > limitResult && (
				<div style={{marginTop: 10}}>
					<IconButton
						iconProps={{iconName: 'DoubleChevronDown'}}
						onClick={() => {
							setLimitResult(
								limitResult + (detlaShownItems ?? NUM_DEFAULT_DELTA),
							);
						}}
					/>
					<i>
						{t('MoreResults', {num: `${childrenFinal.length - limitResult}`})}
					</i>
				</div>
			)}
		</Stack>
	);
};

const searchGroupClassNames = (theme: Theme) =>
	mergeStyleSets({
		group: {
			width: '70vw',
			padding: '10px',
			boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
			borderRadius: 15,
			margin: 'auto',
			selectors: {
				'&:hover': {
					background: theme.palette.neutralLight,
				},
			},
		},
		heading: {
			fontSize: '15px',
			fontWeight: '600',
			lineHeight: 30,
		},
		itemCount: {
			display: 'inline-flex',
			justifyContent: 'center',
			alignItems: 'center',
			minWidth: 30,
			height: 25,
			marginLeft: 5,
			marginTop: 2,
			borderRadius: '50%',
			backgroundColor: theme.palette.themePrimary,
			color: theme.palette.white,
			fontSize: 15,
			fontWeight: 'bold',
		},
		children: {
			display: 'flex',
			gap: '12px',
		},
	});
